"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dereferencePipeline = exports.flattenPipeline = void 0;
/**
 * Flattens the pipeline by replacing all pipeline references with their parent pipelines. Does not dereference pipelines in operations.
 * @param pipeline The pipeline to flatten
 * @param exploration The exploration to search in
 * @returns The flattened pipeline
 */
function flattenPipeline(pipeline, exploration) {
    if ('parentId' in pipeline) {
        const { pipelineId, ...parentPipeline } = getPipelineById(pipeline.parentId, exploration);
        return flattenPipeline({
            ...parentPipeline,
            operations: [...parentPipeline.operations, ...pipeline.operations],
            ...('pipelineId' in pipeline && { pipelineId: pipeline.pipelineId }),
        }, exploration);
    }
    if ('parent' in pipeline) {
        const { pipelineId, ...parentPipeline } = pipeline.parent;
        return flattenPipeline({
            ...parentPipeline,
            operations: [...parentPipeline.operations, ...pipeline.operations],
            ...('pipelineId' in pipeline && { pipelineId: pipeline.pipelineId }),
        }, exploration);
    }
    return pipeline;
}
exports.flattenPipeline = flattenPipeline;
/**
 * Dereferences a pipeline by replacing all pipeline references with their parent pipelines. Also dereferences pipelines in operations.
 * @param pipeline The pipeline to dereference
 * @param exploration The exploration to search in
 * @returns The dereferenced pipeline
 */
function dereferencePipeline(pipeline, exploration) {
    const flattenedPipeline = flattenPipeline(pipeline, exploration);
    return {
        ...flattenedPipeline,
        operations: dereferenceOperations(flattenedPipeline.operations, exploration),
    };
}
exports.dereferencePipeline = dereferencePipeline;
/**
 * Returns the pipeline with the given id from the exploration.
 * @param pipelineId The id of the pipeline to find
 * @param exploration The exploration to search in
 * @returns The pipeline with the given id
 */
const getPipelineById = (pipelineId, exploration) => {
    const cell = exploration.view.cells.find((cell) => 'pipeline' in cell && 'pipelineId' in cell.pipeline && cell.pipeline.pipelineId === pipelineId);
    if (cell === undefined || !('pipeline' in cell) || !('pipelineId' in cell.pipeline)) {
        throw new Error(`Could not find pipeline with id ${pipelineId}`);
    }
    if ('parentId' in cell.pipeline || 'baseModelId' in cell.pipeline) {
        return cell.pipeline;
    }
    throw new Error('Invalid pipeline');
};
/**
 * Checks if a funnel step is empty. A funnel step is empty if it has no modelId or pipelineId.
 * @param step The funnel step to check
 * @returns True if the funnel step is empty, false otherwise
 */
const isEmptyFunnelStep = (step) => !('baseModelId' in step.pipeline && step.pipeline.baseModelId !== '') &&
    !('parentId' in step.pipeline && step.pipeline.parentId !== '');
/**
 * Dereferences a funnel operation.
 * @param operation The funnel operation to dereference
 * @param exploration The exploration to search in
 * @returns The dereferenced funnel operation
 */
const dereferenceFunnelOperation = (operation, exploration) => ({
    ...operation,
    parameters: {
        ...operation.parameters,
        steps: operation.parameters.steps
            .filter((step) => !isEmptyFunnelStep(step))
            .map((step) => {
            const pipeline = getDereferencedStepPipeline(step, exploration);
            return {
                modelId: pipeline.baseModelId,
                operations: pipeline.operations,
                sortKey: step.sortKey ?? null,
                fields: step.fields ?? [],
            };
        }),
    },
});
/**
 *
 * @param step The funel step to dereference
 * @param exploration The exploration to search in
 * @returns The dereferenced funnel step
 */
const getDereferencedStepPipeline = (step, exploration) => dereferencePipeline(step.pipeline, exploration);
/**
 * Dereferences pipelines in operations.
 * @param operations The operations to dereference
 * @param exploration The exploration to search in
 * @returns The dereferenced operations
 */
const dereferenceOperations = (operations, exploration) => {
    return operations.map((operation) => {
        switch (operation.operation) {
            case 'addRelatedColumn': {
                if (operation.parameters.pipelineId === undefined) {
                    const parameters = operation.parameters;
                    return {
                        ...operation,
                        parameters: { ...parameters },
                    };
                }
                const subPipeline = getPipelineById(operation.parameters.pipelineId, exploration);
                const { pipelineId, ...parameters } = operation.parameters;
                return {
                    ...operation,
                    parameters: {
                        ...parameters,
                        pipeline: dereferencePipeline(subPipeline, exploration),
                    },
                };
            }
            case 'relationAggregate': {
                if (operation.parameters.pipelineId !== undefined) {
                    const { pipelineId, ...restParameters } = operation.parameters;
                    return {
                        ...operation,
                        parameters: {
                            ...restParameters,
                            pipeline: dereferencePipeline(getPipelineById(pipelineId, exploration), exploration),
                        },
                    };
                }
                if ('joinStrategy' in operation.parameters) {
                    const { pipelineId, ...restParameters } = operation.parameters;
                    return {
                        ...operation,
                        parameters: {
                            ...restParameters,
                            pipeline: dereferencePipeline(getPipelineById(pipelineId, exploration), exploration),
                        },
                    };
                }
                const { pipelineId, ...restParameters } = operation.parameters;
                return {
                    ...operation,
                    parameters: {
                        ...restParameters,
                    },
                };
            }
            case 'funnel':
                return dereferenceFunnelOperation(operation, exploration);
            case 'cohort':
                return {
                    ...operation,
                    parameters: {
                        ...operation.parameters,
                        pipeline: dereferencePipeline(operation.parameters.pipeline, exploration),
                    },
                };
            case 'joinPipeline':
                return {
                    ...operation,
                    parameters: {
                        ...operation.parameters,
                        pipeline: dereferencePipeline(operation.parameters.pipeline, exploration),
                    },
                };
            default:
                return operation;
        }
    });
};
//# sourceMappingURL=utils.js.map